import { ObjectType, type IObject, type IObjectData } from "./types";
import type {
	IPoint,
	IFlatGroundSavedData,
	IGeometry,
} from "earthbrain-viewer";
import store from "@/store";
import type { IFlatgroundObjectData_Version1 } from "./FlatgroundObjectAdapter";
import { uploadObjAndNotify } from "@/utils/cesium-common";
import { md5 } from "hash-wasm";
import { i18n } from "@/i18n.js";
import { distanceUnits, type DistanceUnit } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";

type FlatgroundDistanceDefaults = {
	interpPitch: number;
	cutHeight: number;
	cutShelfWidth: number;
	fillHeight: number;
	fillShelfWidth: number;
};

const FLATGROUND_FEET_DISPLAY_DEFAULTS: FlatgroundDistanceDefaults = {
	interpPitch: 16,
	cutHeight: 24,
	cutShelfWidth: 5,
	fillHeight: 16,
	fillShelfWidth: 5,
};

/** Display defaults per distance unit (distance fields only; dimensionless params excluded) */
const FLATGROUND_DISPLAY_DEFAULTS: Record<
	DistanceUnit,
	FlatgroundDistanceDefaults
> = {
	[distanceUnits.METERS]: {
		interpPitch: 5,
		cutHeight: 7,
		cutShelfWidth: 1.5,
		fillHeight: 5,
		fillShelfWidth: 1.5,
	},
	[distanceUnits.FEET]: FLATGROUND_FEET_DISPLAY_DEFAULTS,
	[distanceUnits.US_SURVEY_FEET]: FLATGROUND_FEET_DISPLAY_DEFAULTS,
};

export interface IFlatgroundObjectData extends IObjectData {
	id: string;
	name: string;
	panel: {
		shelf: boolean;
		volume: boolean;
		cutFlat: boolean;
		flatGround: boolean;
	};
	cutVolume: number;
	fillVolume: number;
	surfaceArea: number;
	transparency: number;
	maxHeight: number;
	surfaceSettings: {
		interpolationPitch: number;
		withSlope: boolean;
		color: string;
	};
	cutSettings: {
		height: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		generateShelf: boolean;
		color: string;
	};
	fillSettings: {
		height: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		generateShelf: boolean;
		color: string;
	};
	associatedTerrain: { id: number; terrainType: string };

	points: IPoint[];
}

export class FlatgroundObject implements IObject, IFlatgroundObjectData {
	readonly type: ObjectType = ObjectType.FLAT_GROUND;
	id: string;
	md5: string;
	name: string;
	associatedTerrain: { id: number; terrainType: string };
	isOpen: boolean;
	panel: {
		shelf: boolean;
		volume: boolean;
		cutFlat: boolean;
		flatGround: boolean;
	};
	cutVolume: number;
	fillVolume: number;
	surfaceArea: number;
	transparency: number;
	maxHeight: number;
	surfaceSettings: {
		interpolationPitch: number;
		withSlope: boolean;
		color: string;
	};
	cutSettings: {
		height: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		generateShelf: boolean;
		color: string;
	};
	fillSettings: {
		height: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		generateShelf: boolean;
		color: string;
	};

	isShow: boolean;
	isLoaded: boolean;
	isHiddenDuringEdit: boolean;
	loadingVolume: boolean;
	points: IPoint[];

	get default(): IFlatgroundObjectData {
		const unit = store.state.site?.units?.distance ?? distanceUnits.METERS;
		const distanceDefaults = FLATGROUND_DISPLAY_DEFAULTS[unit];

		const unitPort = createDistanceUnitPort(unit);
		const toInternal = (v: number) => unitPort.toMeters(v);

		return {
			id: null,
			type: ObjectType.FLAT_GROUND,
			md5: "",
			name: "",
			panel: {
				shelf: false,
				volume: false,
				cutFlat: false,
				flatGround: false,
			},
			cutVolume: 0,
			fillVolume: 0,
			surfaceArea: 0,
			transparency: 1,
			maxHeight: 0,
			surfaceSettings: {
				interpolationPitch: toInternal(distanceDefaults.interpPitch),
				withSlope: true,
				color: "#DDDDDD",
			},
			cutSettings: {
				height: toInternal(distanceDefaults.cutHeight),
				moundSlope: 1,
				shelfWidth: toInternal(distanceDefaults.cutShelfWidth),
				shelfSlope: 100,
				generateShelf: true,
				color: "#FFA500",
			},
			fillSettings: {
				height: toInternal(distanceDefaults.fillHeight),
				moundSlope: 1.5,
				shelfWidth: toInternal(distanceDefaults.fillShelfWidth),
				shelfSlope: 100,
				generateShelf: true,
				color: "#008000",
			},
			associatedTerrain: {
				id: null,
				terrainType: "",
			},
			points: [],
		};
	}

	constructor() {
		this.isShow = false;
		this.isLoaded = false;
		this.isHiddenDuringEdit = false;
		this.isOpen = false;
		this.loadingVolume = false;
		this.id = this.default.id;
		this.name = this.default.name;
		this.panel = this.default.panel;
		this.cutVolume = this.default.cutVolume;
		this.fillVolume = this.default.fillVolume;
		this.surfaceArea = this.default.surfaceArea;
		this.transparency = this.default.transparency;
		this.maxHeight = this.default.maxHeight;
		this.surfaceSettings = this.default.surfaceSettings;
		this.cutSettings = this.default.cutSettings;
		this.fillSettings = this.default.fillSettings;
		this.associatedTerrain = this.default.associatedTerrain;
		this.points = this.default.points;
	}

	async update(
		data: Partial<IFlatgroundObjectData>,
		updateInViewer: boolean = false,
	) {
		this.id = data.id;
		this.name = data.name ?? this.name ?? this.default.name;
		this.panel.shelf =
			data.panel.shelf ?? this.panel.shelf ?? this.default.panel.shelf;
		this.panel.volume =
			data.panel.volume ?? this.panel.volume ?? this.default.panel.volume;
		this.panel.cutFlat =
			data.panel.cutFlat ?? this.panel.cutFlat ?? this.default.panel.cutFlat;
		this.panel.flatGround =
			data.panel.flatGround ??
			this.panel.flatGround ??
			this.default.panel.flatGround;
		this.cutVolume = data.cutVolume ?? this.cutVolume ?? this.default.cutVolume;
		this.fillVolume =
			data.fillVolume ?? this.fillVolume ?? this.default.fillVolume;
		this.surfaceArea =
			data.surfaceArea ?? this.surfaceArea ?? this.default.surfaceArea;
		this.transparency =
			data.transparency ?? this.transparency ?? this.default.transparency;
		this.maxHeight = data.maxHeight ?? this.maxHeight ?? this.default.maxHeight;
		this.surfaceSettings.interpolationPitch =
			data.surfaceSettings.interpolationPitch ??
			this.surfaceSettings.interpolationPitch ??
			this.default.surfaceSettings.interpolationPitch;
		this.surfaceSettings.withSlope =
			data.surfaceSettings.withSlope ??
			this.surfaceSettings.withSlope ??
			this.default.surfaceSettings.withSlope;
		this.surfaceSettings.color =
			data.surfaceSettings.color ??
			this.surfaceSettings.color ??
			this.default.surfaceSettings.color;
		this.cutSettings.height =
			data.cutSettings.height ??
			this.cutSettings.height ??
			this.default.cutSettings.height;
		this.cutSettings.moundSlope =
			data.cutSettings.moundSlope ??
			this.cutSettings.moundSlope ??
			this.default.cutSettings.moundSlope;
		this.cutSettings.shelfWidth =
			data.cutSettings.shelfWidth ??
			this.cutSettings.shelfWidth ??
			this.default.cutSettings.shelfWidth;
		this.cutSettings.shelfSlope =
			data.cutSettings.shelfSlope ??
			this.cutSettings.shelfSlope ??
			this.default.cutSettings.shelfSlope;
		this.cutSettings.generateShelf =
			data.cutSettings.generateShelf ??
			this.cutSettings.generateShelf ??
			this.default.cutSettings.generateShelf;
		this.cutSettings.color =
			data.cutSettings.color ??
			this.cutSettings.color ??
			this.default.cutSettings.color;
		this.fillSettings.height =
			data.fillSettings.height ??
			this.fillSettings.height ??
			this.default.fillSettings.height;
		this.fillSettings.moundSlope =
			data.fillSettings.moundSlope ??
			this.fillSettings.moundSlope ??
			this.default.fillSettings.moundSlope;
		this.fillSettings.shelfWidth =
			data.fillSettings.shelfWidth ??
			this.fillSettings.shelfWidth ??
			this.default.fillSettings.shelfWidth;
		this.fillSettings.shelfSlope =
			data.fillSettings.shelfSlope ??
			this.fillSettings.shelfSlope ??
			this.default.fillSettings.shelfSlope;
		this.fillSettings.generateShelf =
			data.fillSettings.generateShelf ??
			this.fillSettings.generateShelf ??
			this.default.fillSettings.generateShelf;
		this.fillSettings.color =
			data.fillSettings.color ??
			this.fillSettings.color ??
			this.default.fillSettings.color;
		this.associatedTerrain.id =
			data.associatedTerrain.id ??
			this.associatedTerrain.id ??
			this.default.associatedTerrain.id;
		this.associatedTerrain.terrainType =
			data.associatedTerrain.terrainType ??
			this.associatedTerrain.terrainType ??
			this.default.associatedTerrain.terrainType;
		this.points = data.points ?? this.points ?? this.default.points;

		if (updateInViewer) {
			await this.updateSettingsInViewer();
		}
	}

	getSettingsForViewer() {
		return {
			commonSettings: {
				interpolationPitch: this.surfaceSettings.interpolationPitch,
				withSlope: this.surfaceSettings.withSlope,
				surfaceColor: this.surfaceSettings.color,
				transparency: this.transparency,
			},
			cutSettings: {
				height: this.cutSettings.height,
				moundSlope: this.cutSettings.moundSlope,
				shelfWidth: this.cutSettings.shelfWidth,
				shelfSlope: this.cutSettings.shelfSlope,
				generateShelf: this.cutSettings.generateShelf,
				material: this.cutSettings.color,
				transparency: this.transparency,
			},
			embankmentSettings: {
				height: this.fillSettings.height,
				moundSlope: this.fillSettings.moundSlope,
				shelfWidth: this.fillSettings.shelfWidth,
				shelfSlope: this.fillSettings.shelfSlope,
				generateShelf: this.fillSettings.generateShelf,
				material: this.fillSettings.color,
				transparency: this.transparency,
			},
		};
	}

	public async updateMaterialSettings(updateInViewer: boolean = false) {
		if (updateInViewer) {
			await store.getters.viewer.updateFlatGroundMaterialSettings(
				this.id,
				{
					commonSettings: {
						surfaceColor: this.surfaceSettings.color,
						transparency: this.transparency,
					},
				},
				{ material: this.fillSettings.color, transparency: this.transparency },
				{ material: this.cutSettings.color, transparency: this.transparency },
			);
		}
	}

	public async updateTransparency() {
		await store.getters.viewer.updateFlatGroundMaterialSettings(
			this.id,
			{
				commonSettings: {
					surfaceColor: this.surfaceSettings.color,
					transparency: this.transparency,
				},
			},
			{ transparency: this.transparency },
			{ transparency: this.transparency },
		);
	}

	getViewerJSON(): IFlatGroundSavedData {
		return {
			flatGroundAreaId: this.id,
			points: this.points,
			flatGroundSettings: this.getSettingsForViewer(),
			associatedTerrain: {
				id: this.associatedTerrain.id,
				terrainType: this.associatedTerrain.terrainType as any,
			},
			rubbingDestination: {
				target: this.associatedTerrain.terrainType as any,
				id: this.associatedTerrain.id,
			},
		};
	}

	async loadInViewer(): Promise<void> {
		try {
			this.id = await store.getters.viewer.loadFlatGroundArea(
				this.getViewerJSON(),
			);
			const maxHeight = store.getters.viewer.getMaxHeightOfFlatGroundCtrlPts(
				this.id,
			);
			this.maxHeight = Number.parseFloat(maxHeight.toFixed(3));
			this.isLoaded = true;
			this.isHiddenDuringEdit = false;
			this.isShow = true;
		} catch (e) {
			console.error(e);
			this.isShow = false;
			this.isLoaded = false;
			this.isHiddenDuringEdit = false;
		}
	}

	private shouldBlockRestoreOnInvalidEditForm(): boolean {
		return (
			store.state.object.objectUnderEdit?.id === this.id &&
			store.state.object.isEditFormValid === false
		);
	}

	private showInvalidEditFormSnackbar(): void {
		store.commit("set_snackbar", {
			text: i18n.global.t("CANNOT_SHOW_OBJECT_INVALID_VALUES"),
			color: "rgba(153, 0, 0, 0.72)",
			timeout: -1,
		});
	}

	async setVisibility(visible: boolean): Promise<boolean> {
		if (visible && this.isHiddenDuringEdit) {
			if (this.shouldBlockRestoreOnInvalidEditForm()) {
				this.showInvalidEditFormSnackbar();
				return false;
			}

			const flatGroundJson = store.getters.viewer.getFlatGroundJSON(this.id);
			if (flatGroundJson) {
				// Hidden during edit: FlatGround still exists in viewer memory.
				await store.getters.viewer.restoreFlatGroundAfterHiding(this.id);
				// Apply panel-side changes made while hidden.
				await this.updateSettingsInViewer();
				this.isHiddenDuringEdit = false;
				this.isShow = true;
				return true;
			}
			// Viewer-side object unexpectedly missing; reset and reload below.
			this.isHiddenDuringEdit = false;
			this.isLoaded = false;
		}

		if (visible && !this.isLoaded) {
			await this.loadInViewer();
			if (!this.isLoaded) {
				return false;
			}
		}
		store.getters.viewer.toggleFlatGroundVisibility(this.id, visible);
		this.isShow = visible;
		if (!visible && store.state.object.objectUnderEdit?.id === this.id) {
			this.isHiddenDuringEdit = true;
		}
		return true;
	}

	startEdit(): void {
		const dispatch = store.dispatch.bind(store);
		dispatch("flatGround/setIsFlatGroundHoriDrag", false, { root: true });
		dispatch("flatGround/setIsFlatGroundVertiDrag", false, {
			root: true,
		});
		dispatch("flatGround/setIsFlatGroundUniformDrag", false, {
			root: true,
		});
		store.getters.viewer.enableFlatGroundEdit(true, this.id, () => {
			this.updateFromViewer();
		});
	}
	async endEdit(): Promise<void> {
		const dispatch = store.dispatch.bind(store);
		dispatch("flatGround/setIsFlatGroundHoriDrag", false, { root: true });
		dispatch("flatGround/setIsFlatGroundVertiDrag", false, { root: true });
		dispatch("flatGround/setIsFlatGroundUniformDrag", false, { root: true });
		await store.getters.viewer.enableFlatGroundEdit(false, this.id, () => {});
		this.updateFromViewer();
		const propsJSON = await this.toJSON();
		await uploadObjAndNotify(propsJSON, this.type);
	}
	deleteObject(): void {
		store.getters.viewer.removePolygon(this.id);
	}
	getGeometry(): IGeometry {
		return store.getters.viewer.getFlatGroundMeshGeometry(this.id);
	}

	fitToView(): void {
		store.getters.viewer.fitToFlatGround(this.id);
	}

	async updateSettingsInViewer() {
		await store.getters.viewer.updateFlatGroundSettings(
			this.id,
			this.getSettingsForViewer(),
		);

		this.updateFromViewer();
	}

	updateFromViewer() {
		const flatgroundViewerObj: IFlatGroundSavedData =
			store.getters.viewer.getFlatGroundJSON(this.id);
		if (!flatgroundViewerObj) {
			return;
		}
		this.points = flatgroundViewerObj.points;
		this.associatedTerrain = {
			id: flatgroundViewerObj.associatedTerrain.id,
			terrainType: flatgroundViewerObj.associatedTerrain.terrainType as any,
		};
		const maxHeight = store.getters.viewer.getMaxHeightOfFlatGroundCtrlPts(
			this.id,
		);
		this.maxHeight = Number.parseFloat(maxHeight.toFixed(3));
	}

	async updateAssociatedTerrain(terrainId: number, terrainType: string) {
		this.associatedTerrain = {
			id: terrainId,
			terrainType: terrainType as any,
		};
		await store.getters.viewer.updateTerrainAssociatedWithFlatGround(
			this.id,
			terrainId,
			terrainType as any,
		);
		await store.getters.viewer.updateRubbingDestinationAssociatedWithFlatGround(
			this.id,
			{
				target: terrainType as any,
				id: terrainId,
			},
		);
	}

	async toJSON(): Promise<IFlatgroundObjectData_Version1> {
		this.updateFromViewer();
		const json = {
			id: this.id,
			cid: this.id,
			type: this.type,
			name: this.name,
			panel: this.panel,
			cutVolume: this.cutVolume,
			fillVolume: this.fillVolume,
			surfaceArea: this.surfaceArea,
			transparency: this.transparency,
			maxHeight: this.maxHeight,
			surfaceSettings: this.surfaceSettings,
			cutSettings: this.cutSettings,
			fillSettings: this.fillSettings,
			associatedTerrain: this.associatedTerrain,
			points: this.points,
		};
		return {
			version: 1.0,
			...json,
			md5: await md5(JSON.stringify(json)),
		};
	}

	async toDuplicateJSON(): Promise<IFlatgroundObjectData_Version1> {
		this.updateFromViewer();
		const newId = crypto.randomUUID();
		const json = {
			id: newId,
			cid: newId,
			type: this.type,
			name: this.name + " copy",
			panel: this.panel,
			cutVolume: this.cutVolume,
			fillVolume: this.fillVolume,
			surfaceArea: this.surfaceArea,
			transparency: this.transparency,
			maxHeight: this.maxHeight,
			surfaceSettings: this.surfaceSettings,
			cutSettings: this.cutSettings,
			fillSettings: this.fillSettings,
			associatedTerrain: this.associatedTerrain,
			points: this.points,
		};
		return {
			version: 1.0,
			...json,
			md5: await md5(JSON.stringify(json)),
		};
	}
}
